import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { ApiErrorResponse } from '@/lib/api-client';

/**
 * Default stale time for queries (5 minutes).
 * Data is considered fresh for this duration and won't be refetched.
 */
const DEFAULT_STALE_TIME = 5 * 60 * 1000;

/**
 * Default garbage collection time (10 minutes).
 * Inactive cache entries are removed after this duration.
 */
const DEFAULT_GC_TIME = 10 * 60 * 1000;

/**
 * Determine if a failed request should be retried based on the error status.
 * Don't retry on 4xx client errors (except 408 Request Timeout and 429 Too Many Requests).
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;

  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
      return false;
    }
  }

  return true;
}

/**
 * Global error handler for query cache errors.
 * Dispatches events for session expiry so the auth layer can react.
 */
function handleQueryError(error: unknown) {
  if (error instanceof AxiosError && error.response?.status === 401) {
    // Session expired — the API client interceptor handles token refresh,
    // so a 401 reaching here means refresh also failed.
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
  }
}

/**
 * TanStack Query client configured for the platform.
 *
 * Features:
 * - 5-minute stale time to reduce unnecessary refetches
 * - Smart retry logic that skips client errors
 * - No refetch on window focus (explicit user action preferred)
 * - Global error handling for session expiry
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleQueryError,
  }),
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      gcTime: DEFAULT_GC_TIME,
      retry: shouldRetry,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Invalidate all queries — useful after login/logout to reset cache.
 */
export function invalidateAllQueries(): Promise<void> {
  return queryClient.invalidateQueries();
}

/**
 * Clear the entire query cache — used on logout.
 */
export function clearQueryCache(): void {
  queryClient.clear();
}

/**
 * Extract a user-friendly error message from an API error.
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const apiError = error.response?.data as ApiErrorResponse | undefined;

    // Prefer specific field-level validation messages when present. The API
    // returns a generic top-level message (e.g. "Request validation failed")
    // and the actionable reason(s) inside error.details[].message.
    const detailMessage = extractValidationDetailMessage(apiError?.error?.details);
    if (detailMessage) {
      return detailMessage;
    }

    if (apiError?.error?.message) {
      return apiError.error.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Extracts human-readable messages from a validation error's `details` array.
 * Joins multiple field errors so the user sees every problem at once.
 */
function extractValidationDetailMessage(details: unknown): string | null {
  if (!Array.isArray(details)) return null;

  const messages = details
    .map((d) =>
      d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string'
        ? (d as { message: string }).message
        : null,
    )
    .filter((m): m is string => !!m);

  if (messages.length === 0) return null;
  return messages.join('\n');
}
